import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { 
  PlusCircle, Filter, Clipboard, Search, CheckSquare, 
  Package, Clock, AlertCircle, ChevronRight, Eye, Edit, Trash2,
  X, CheckCircle, ListChecks
} from 'lucide-react';
import { getRequisitions, updateRequisition } from '../../api/procurementApi';
import RequisitionFormModal from '../../components/RequisitionFormModal';
import SidebarNav from '../../components/MasterSideBar';
import TopbarNav from "../../components/TopbarNav";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumCard from "../../components/common/PremiumCard";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import { toast } from 'react-toastify';
import Select from 'react-select';
import { Dialog, Transition } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import RequisitionsTable from '../../components/PDRequisitionsTable';
import ViewRequisitionModal, { ViewRequisitionContent } from '../../components/programManager/ViewRequisitionModal';

const EXECUTIVE_APPROVAL_THRESHOLD = 1000000;
const REQUISITION_STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Awaiting My Approval' },
  { value: 'pending_director_approval', label: 'Pending My Approval' },
  { value: 'approved_by_director', label: 'Approved by Me' },
  { value: 'rejected_by_director', label: 'Rejected by Me' },
  { value: 'pending_executive_approval', label: 'Pending Executive Approval' },
  { value: 'processing_by_procurement', label: 'Processing by Procurement' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'on_hold', label: 'On Hold' },
];

const DirectorProcurementDashboard = () => {
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(REQUISITION_STATUS_FILTER_OPTIONS[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [currentRequisitionForAction, setCurrentRequisitionForAction] = useState(null);
  const [actionType, setActionType] = useState('');
  const [comments, setComments] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [requisitionToView, setRequisitionToView] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const fetchRequisitions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRequisitions();
      const requisitionsData = data.data?.data || [];
      setRequisitions(requisitionsData);
    } catch (err) {
      setError("Failed to load requisitions.");
      toast.error("Failed to load requisitions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequisitions();
  }, []);

  const handleOpenCommentsModal = (requisition, type) => {
    setCurrentRequisitionForAction(requisition);
    setActionType(type);
    setComments('');
    setShowCommentsModal(true);
  };

  const handleCloseCommentsModal = () => {
    setShowCommentsModal(false);
    setCurrentRequisitionForAction(null);
    setActionType('');
    setComments('');
  };

  const handleSubmitDirectorAction = async () => {
    if (!currentRequisitionForAction) return;
    setLoading(true);

    try {
      const payload = {
        directorApproval: {
          status: actionType === 'approve' ? 'approved' : 'rejected',
          comments: comments
        }
      };

      const updatedRequisition = await updateRequisition(currentRequisitionForAction._id, payload);
      toast.success(updatedRequisition.message || `Requisition ${actionType}ed successfully!`);
      handleCloseCommentsModal();
      fetchRequisitions();
    } catch (error) {
      toast.error(`Failed to ${actionType} requisition.`);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequisitions = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return requisitions.filter(req => {
      const matchesStatus = selectedStatusFilter.value === 'all' || req.currentStatus === selectedStatusFilter.value;
      const matchesSearch = req.title.toLowerCase().includes(query) || 
                            (req.projectId?.projectTitle || '').toLowerCase().includes(query);
      return matchesStatus && matchesSearch;
    });
  }, [requisitions, selectedStatusFilter, searchQuery]);

  // Quick stats calculations
  const totalSubmissions = requisitions.length;
  const pendingApproval = requisitions.filter(r => r.currentStatus === 'submitted' || r.currentStatus === 'pending_director_approval').length;
  const actionedByMe = requisitions.filter(r => r.currentStatus === 'approved_by_director' || r.currentStatus === 'rejected_by_director').length;

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '1.25rem',
      padding: '0.5rem',
      border: '1px solid #f3f4f6',
      fontSize: '11px',
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      boxShadow: 'none',
      '&:hover': { border: '1px solid #e5e7eb' }
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '11px',
      fontWeight: '800',
      textTransform: 'uppercase',
      backgroundColor: state.isSelected ? '#111827' : state.isFocused ? '#f9fafb' : 'transparent',
      color: state.isSelected ? 'white' : '#374151',
    })
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <SidebarNav 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex flex-col flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <TopbarNav 
            onSearchChange={setSearchQuery} 
            onSidebarToggle={() => setSidebarOpen(true)} 
        />

        <main className="flex-1 overflow-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            
            <div className="flex flex-col items-center text-center mb-12">
                <PremiumHeader 
                  title="Directorate" 
                  lastWordAccent="Procurement" 
                  subtitle="Verify, approve, and oversee project requisitions across all active programs." 
                />
                <div className="flex flex-wrap justify-center gap-4 mt-8">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center gap-3 bg-gray-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-orange-600 shadow-2xl shadow-gray-200 transition-all active:scale-95"
                  >
                    <PlusCircle size={16} strokeWidth={3} /> Issue Requisition
                  </motion.button>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <PremiumStatCard 
                  title="Pipeline Volume" 
                  value={totalSubmissions} 
                  icon={<Package className="text-orange-600" />} 
                  iconBg="bg-orange-50"
                  description="Total project requests"
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <PremiumStatCard 
                  title="Action Required" 
                  value={pendingApproval} 
                  icon={<Clock className="text-blue-600" />} 
                  iconBg="bg-blue-50"
                  description="Awaiting your review"
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <PremiumStatCard 
                  title="Resolved Decisions" 
                  value={actionedByMe} 
                  icon={<CheckCircle className="text-emerald-600" />} 
                  iconBg="bg-emerald-50"
                  description="Actioned this period"
                />
              </motion.div>
            </div>

            <div className="space-y-8">
              <PremiumCard icon={<Filter size={18} />} title="Decision Pipeline Filters">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-4">
                  <div className="space-y-2">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Requisition Status</p>
                    <Select
                      options={REQUISITION_STATUS_FILTER_OPTIONS}
                      value={selectedStatusFilter}
                      onChange={setSelectedStatusFilter}
                      styles={selectStyles}
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Archive Search</p>
                    <div className="relative group">
                      <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-orange-500 transition-all" size={14} />
                      <input 
                        type="text" 
                        placeholder="Filter by title or project reference..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-white border border-gray-100 rounded-2xl pl-12 pr-6 py-4 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-gray-900 focus:outline-none transition-all shadow-sm placeholder:text-gray-300"
                      />
                    </div>
                  </div>
                </div>
              </PremiumCard>

              <PremiumCard icon={<ListChecks size={18} />} title="Requisition Priority Queue">
                <div className="overflow-hidden p-2">
                  <RequisitionsTable
                    requisitions={filteredRequisitions}
                    loading={loading}
                    error={error}
                    handleOpenCommentsModal={handleOpenCommentsModal}
                    handleViewRequisitionDetails={setRequisitionToView}
                    EXECUTIVE_APPROVAL_THRESHOLD={EXECUTIVE_APPROVAL_THRESHOLD}
                    REQUISITION_STATUS_FILTER_OPTIONS={REQUISITION_STATUS_FILTER_OPTIONS}
                  />
                </div>
              </PremiumCard>
            </div>
          </div>
        </main>
      </div>

      <RequisitionFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSaveSuccess={fetchRequisitions}
      />

      {/* Action Comments Modal */}
      <AnimatePresence>
        {showCommentsModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              onClick={handleCloseCommentsModal} 
              className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.95, opacity: 0, y: 20 }} 
              className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md p-10 overflow-hidden border border-gray-100"
            >
              <div className="mb-8 text-center">
                <div className={`size-16 rounded-full flex items-center justify-center mx-auto mb-6 ${actionType === 'approve' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                  <Clipboard size={32} />
                </div>
                <h3 className="text-xs font-black text-gray-800 uppercase tracking-[0.2em]">
                  {actionType} Requisition
                </h3>
                <p className="text-[10px] font-bold text-gray-400 uppercase mt-2">Administrative feedback for this decision</p>
              </div>
              
              <div className="mt-4">
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows="5"
                  className="w-full bg-gray-50 border-none rounded-2xl p-6 text-sm font-bold text-gray-700 focus:ring-2 focus:ring-orange-500 outline-none transition-all shadow-inner placeholder:text-gray-300"
                  placeholder={`Reason for ${actionType}al...`}
                />
              </div>
              
              <div className="mt-10 flex gap-4">
                <button 
                  onClick={handleCloseCommentsModal} 
                  className="flex-1 bg-gray-50 text-gray-400 px-6 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-100 hover:text-gray-800 transition-all active:scale-95"
                >
                  Dismiss
                </button>
                <button 
                  onClick={handleSubmitDirectorAction} 
                  className={`flex-1 text-white px-6 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all active:scale-95 shadow-xl ${actionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-100' : 'bg-red-600 hover:bg-red-700 shadow-red-100'}`}
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* View Details Modal */}
      <AnimatePresence>
        {requisitionToView && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              onClick={() => setRequisitionToView(null)} 
              className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.95, opacity: 0, y: 20 }} 
              className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto border border-gray-100 custom-scrollbar"
            >
              <ViewRequisitionContent
                requisition={requisitionToView}
                handleOpenCommentsModal={handleOpenCommentsModal}
                EXECUTIVE_APPROVAL_THRESHOLD={EXECUTIVE_APPROVAL_THRESHOLD}
                onRequestClose={() => setRequisitionToView(null)}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DirectorProcurementDashboard;